/**
 * Tawk.to Script Loader Utility
 * Manages lazy loading and lifecycle of the Tawk.to chat widget
 * Implements singleton pattern to prevent duplicate script loads
 */

import logger from './logger.js';

class TawkToLoader {
  constructor() {
    this.scriptId = 'tawk-to-script';
    this.scriptUrl = 'https://embed.tawk.to/686936a7d7341f1911949856/1ivdfr02l';
    this.loadPromise = null;
    this.isLoading = false;
    this.isLoaded = false;
    this.loadAttempts = 0;
    this.maxLoadAttempts = 3;
  }

  /**
   * Load the Tawk.to script if not already loaded
   * @returns {Promise<boolean>} Success status
   */
  async load() {
    // If already loaded, return immediately
    if (this.isLoaded && this.isTawkAvailable()) {
      logger.debug('TawkToLoader: Script already loaded and available', null, 'chat');
      return true;
    }

    // If currently loading, return existing promise
    if (this.loadPromise) {
      logger.debug('TawkToLoader: Script currently loading, waiting...', null, 'chat');
      return this.loadPromise;
    }

    // Start new load attempt
    this.loadPromise = this._loadScript();
    
    try {
      const result = await this.loadPromise;
      return result;
    } finally {
      this.loadPromise = null;
    }
  }

  /**
   * Internal method to actually load the script
   * @private
   * @returns {Promise<boolean>} Success status
   */
  async _loadScript() {
    if (this.loadAttempts >= this.maxLoadAttempts) {
      logger.error('TawkToLoader: Max load attempts reached', { attempts: this.loadAttempts }, 'chat');
      return false;
    }

    this.loadAttempts++;
    this.isLoading = true;

    try {
      logger.info('TawkToLoader: Starting script load', { attempt: this.loadAttempts }, 'chat');

      // Check if script already exists in DOM
      const existingScript = document.getElementById(this.scriptId);
      if (existingScript) {
        logger.debug('TawkToLoader: Removing existing script element', null, 'chat');
        existingScript.remove();
      }

      // Initialize Tawk API
      window.Tawk_API = window.Tawk_API || {};
      window.Tawk_LoadStart = new Date();

      // Create and configure script element
      const script = document.createElement('script');
      script.id = this.scriptId;
      script.async = true;
      script.src = this.scriptUrl;
      script.charset = 'UTF-8';
      script.setAttribute('crossorigin', '*');

      // Create promise to track script loading
      const loadPromise = new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Script load timeout'));
        }, 10000); // 10 second timeout

        script.onload = () => {
          clearTimeout(timeout);
          logger.debug('TawkToLoader: Script element loaded', null, 'chat');
          
          // Wait for Tawk_API to be fully initialized
          this.waitForTawkAPI().then(resolve).catch(reject);
        };

        script.onerror = (error) => {
          clearTimeout(timeout);
          reject(error);
        };
      });

      // Insert script into DOM
      document.head.insertBefore(script, document.head.firstChild);

      // Wait for load to complete
      await loadPromise;

      this.isLoaded = true;
      this.isLoading = false;
      logger.info('TawkToLoader: Script successfully loaded', null, 'chat');
      
      return true;

    } catch (error) {
      this.isLoading = false;
      logger.error('TawkToLoader: Failed to load script', { error, attempt: this.loadAttempts }, 'chat');
      
      // Retry if we haven't exceeded max attempts
      if (this.loadAttempts < this.maxLoadAttempts) {
        logger.debug('TawkToLoader: Retrying load...', { nextAttempt: this.loadAttempts + 1 }, 'chat');
        await this.delay(1000 * this.loadAttempts); // Exponential backoff
        return this._loadScript();
      }
      
      return false;
    }
  }

  /**
   * Wait for Tawk_API to be fully initialized
   * @private
   * @returns {Promise<void>}
   */
  async waitForTawkAPI() {
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds total
    
    while (attempts < maxAttempts) {
      if (this.isTawkAvailable()) {
        logger.debug('TawkToLoader: Tawk_API is available', null, 'chat');
        return;
      }
      
      await this.delay(100);
      attempts++;
    }
    
    throw new Error('Tawk_API initialization timeout');
  }

  /**
   * Check if Tawk.to API is available
   * @returns {boolean} True if available
   */
  isTawkAvailable() {
    return !!(
      window.Tawk_API && 
      (window.Tawk_API.maximize || window.Tawk_API.toggle || window.Tawk_API.hideWidget)
    );
  }

  /**
   * Open/maximize the chat widget
   * @returns {Promise<boolean>} Success status
   */
  async maximize() {
    try {
      // Ensure script is loaded first
      const loaded = await this.load();
      if (!loaded) {
        logger.error('TawkToLoader: Cannot maximize - script not loaded', null, 'chat');
        return false;
      }

      // Try to maximize using API
      if (window.Tawk_API && window.Tawk_API.maximize) {
        window.Tawk_API.maximize();
        logger.debug('TawkToLoader: Chat maximized via API', null, 'chat');
        return true;
      }

      // Fallback: Try to toggle if maximize not available
      if (window.Tawk_API && window.Tawk_API.toggle) {
        window.Tawk_API.toggle();
        logger.debug('TawkToLoader: Chat toggled via API', null, 'chat');
        return true;
      }

      // Last resort: Try to click the widget
      const tawkButton = document.querySelector('[title*="chat"]') || 
                        document.querySelector('.tawk-min-container') ||
                        document.querySelector('[id*="tawk"]');
      
      if (tawkButton) {
        tawkButton.click();
        logger.debug('TawkToLoader: Chat opened via DOM click', null, 'chat');
        return true;
      }

      logger.warn('TawkToLoader: Unable to open chat - no method available', null, 'chat');
      return false;

    } catch (error) {
      logger.error('TawkToLoader: Error maximizing chat', { error }, 'chat');
      return false;
    }
  }

  /**
   * Minimize the chat widget
   * @returns {boolean} Success status
   */
  minimize() {
    if (!this.isLoaded || !window.Tawk_API) {
      logger.warn('TawkToLoader: Cannot minimize - script not loaded', null, 'chat');
      return false;
    }

    if (window.Tawk_API.minimize) {
      window.Tawk_API.minimize();
      logger.debug('TawkToLoader: Chat minimized', null, 'chat');
      return true;
    }

    return false;
  }

  /**
   * Hide the chat widget
   * @returns {boolean} Success status
   */
  hide() {
    if (!this.isLoaded || !window.Tawk_API) {
      logger.warn('TawkToLoader: Cannot hide - script not loaded', null, 'chat');
      return false;
    }

    if (window.Tawk_API.hideWidget) {
      window.Tawk_API.hideWidget();
      logger.debug('TawkToLoader: Chat hidden', null, 'chat');
      return true;
    }

    return false;
  }

  /**
   * Show the chat widget
   * @returns {boolean} Success status
   */
  show() {
    if (!this.isLoaded || !window.Tawk_API) {
      logger.warn('TawkToLoader: Cannot show - script not loaded', null, 'chat');
      return false;
    }

    if (window.Tawk_API.showWidget) {
      window.Tawk_API.showWidget();
      logger.debug('TawkToLoader: Chat shown', null, 'chat');
      return true;
    }

    return false;
  }

  /**
   * Clean up and remove the script
   */
  cleanup() {
    try {
      const script = document.getElementById(this.scriptId);
      if (script) {
        script.remove();
        logger.debug('TawkToLoader: Script removed from DOM', null, 'chat');
      }

      // Reset state
      this.isLoaded = false;
      this.isLoading = false;
      this.loadPromise = null;
      this.loadAttempts = 0;

      // Clear Tawk API
      if (window.Tawk_API) {
        delete window.Tawk_API;
        delete window.Tawk_LoadStart;
      }

      logger.info('TawkToLoader: Cleanup completed', null, 'chat');
    } catch (error) {
      logger.error('TawkToLoader: Error during cleanup', { error }, 'chat');
    }
  }

  /**
   * Utility delay function
   * @private
   * @param {number} ms - Milliseconds to delay
   * @returns {Promise<void>}
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get loader status
   * @returns {Object} Status information
   */
  getStatus() {
    return {
      isLoaded: this.isLoaded,
      isLoading: this.isLoading,
      loadAttempts: this.loadAttempts,
      isTawkAvailable: this.isTawkAvailable(),
      scriptInDOM: !!document.getElementById(this.scriptId)
    };
  }
}

// Export singleton instance
export const tawkToLoader = new TawkToLoader();

// Also export class for testing
export { TawkToLoader };